import { useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";

import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// Convert 24h "HH:MM" -> "h:mm AM/PM"
export function formatTimeLabel(hhmm) {
  if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function parse(value) {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return { h12: 9, m: 0, ampm: "AM" };
  }
  const [h, m] = value.split(":").map(Number);
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { h12, m, ampm };
}

function compose(h12, m, ampm) {
  let h = h12 % 12;
  if (ampm === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

/**
 * Premium time picker: 3 compact wheels (Hour / Minute / AM-PM).
 * Value stored as 24h "HH:MM"; displays 12h AM/PM.
 * Fast, keyboard-friendly, works well on mobile.
 */
export default function TimeSelect({
  value,
  onChange,
  testid = "time-select",
  placeholder = "Select time",
  darkMode = false,
}) {
  const { h12, m, ampm } = useMemo(() => parse(value), [value]);
  const label = useMemo(() => formatTimeLabel(value), [value]);

  const [open, setOpen] = useState(false);
  // Per-open-session tracking: which columns the customer has touched,
  // and whether the field already held a value when the popup opened (edit mode).
  const touched = useRef({ h: false, m: false, a: false });
  const hadValueOnOpen = useRef(false);
  const closeTimer = useRef(null);

  const handleOpenChange = (next) => {
    if (next) {
      touched.current = { h: false, m: false, a: false };
      hadValueOnOpen.current = Boolean(value);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    }
    setOpen(next);
  };

  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  const maybeAutoClose = (isAmPmClick) => {
    const t = touched.current;
    // Editing an existing time: clicking AM/PM confirms the change and closes.
    if (isAmPmClick && hadValueOnOpen.current) {
      closeSoon();
      return;
    }
    // Fresh selection: close only once all three parts have been chosen.
    if (t.h && t.m && t.a) closeSoon();
  };

  const setH = (h) => {
    touched.current.h = true;
    onChange(compose(h, m, ampm));
    maybeAutoClose(false);
  };
  const setM = (mm) => {
    touched.current.m = true;
    onChange(compose(h12, mm, ampm));
    maybeAutoClose(false);
  };
  const setA = (a) => {
    touched.current.a = true;
    onChange(compose(h12, m, a));
    maybeAutoClose(true);
  };

  const triggerClass = darkMode
    ? cn(
        "w-full text-left bg-transparent border-0 outline-none py-1 text-sm",
        value ? "text-white" : "text-white/40",
      )
    : cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C8A15A]/40 transition",
        !value && "text-zinc-400",
      );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-trigger`} className={triggerClass}>
          {darkMode ? (
            <span>{label || placeholder}</span>
          ) : (
            <>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
                {label || placeholder}
              </span>
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3" align="start">
        <div className="grid grid-cols-3 gap-2">
          <Column testid={`${testid}-hour`} label="Hour" values={HOURS} format={(v) => String(v)} selected={h12} onSelect={setH} />
          <Column testid={`${testid}-min`} label="Min" values={MINUTES} format={(v) => String(v).padStart(2, "0")} selected={m} onSelect={setM} />
          <Column testid={`${testid}-ampm`} label="AM/PM" values={["AM", "PM"]} format={(v) => v} selected={ampm} onSelect={setA} narrow />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Column({ testid, label, values, format, selected, onSelect, narrow = false }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-400 text-center pb-1.5">
        {label}
      </span>
      <div
        data-testid={`${testid}-list`}
        className={cn(
          "max-h-[220px] overflow-y-auto rounded-md border border-zinc-200 py-1",
          narrow ? "" : "",
        )}
      >
        {values.map((v) => {
          const isSel = selected === v;
          return (
            <button
              key={String(v)}
              type="button"
              data-testid={`${testid}-opt-${format(v)}`}
              onClick={() => onSelect(v)}
              className={cn(
                "w-full text-center text-sm py-1.5 transition-colors",
                isSel
                  ? "bg-[#C8A15A]/15 text-zinc-900 font-medium"
                  : "text-zinc-600 hover:bg-zinc-100",
              )}
            >
              {format(v)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
